import React, { FC } from 'react';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import type { PageProps } from 'gatsby';
import { Container } from '@agria/theme/src/components/container/Container';
import type { BrandName } from 'packages/types/brand-name';
import { LinkTo } from '@agria/theme/src/components/linkTo/LinkTo';
import { Typography } from '@agria/theme/src/components/typography/Typography';
import { Contact, Menu, Close, Logout } from '@agria/theme/src/components/icon';
import { Button } from '@agria/theme/src/components/button';
import { useHeader } from '@agria/theme/src/context/HeaderContext';
import { useResponsiveType } from '@agria/theme/src/utils';
import { useSiteMetadata } from '@agria/theme/src/hooks/useSiteMetadata';
import { telephoneNumbers } from '@agria/theme/src/const/telephoneNumbers';
import {
  getContactNumberByBrand,
  getQuoteUrlByBrand,
} from '@agria/theme/src/utils/getByBrand';
import { SearchModal } from '@agria/theme/src/components/search/SearchModal';
import { NavDropdown } from '@agria/theme/src/components/navDropdown/NavDropdown';
import { BrandLogo } from '@agria/theme/src/components/brandLogo/BrandLogo';
import type { NavigationItem } from '../header/Header';
import tokens from './tokens';

interface BottomLevelNavProps {
  activePage?: NavigationItem | null;
  brand?: BrandName;
  ctaLink?: string;
  ctaText?: string;
  overrideStickyCTAButton?: {
    label?: string;
    target?: string;
    url?: string;
  };
  enableMyAccount?: boolean;
  isDualBrand?: boolean;
  isMyAccount?: boolean;
  location?: PageProps['location'];
  loggedIn?: boolean; // Set to true when user is in my account context
  myAccountLogout?: () => void; // Function to logout user
  showSearch: boolean;
  siteId?: number;
}

const StyledBottomLevelNav = styled.div`
  ${({ theme: { space } }) => css`
    padding: ${space.large} 0;

    > div {
      display: flex;
      justify-content: space-between;
      align-items: center;
    }
  `};
`;

const StyledBottomLevelNavPrimary = styled.div`
  ${({ theme: { space, maxMedia } }) => css`
    display: flex;
    align-items: center;
    gap: ${space.large};

    @media (max-width: ${maxMedia.small}) {
      margin-right: ${space.small};
      gap: ${space.small};
    }
  `};
`;

const StyledBottomLevelNavItems = styled.nav`
  ${({ theme: { colors, space, maxMedia } }) => css`
    a,
    button {
      text-decoration: none;
      min-height: ${space.large};
      padding-inline: ${space.large};
      display: inline-flex;
      ${useResponsiveType('bodyMediumBold')};
      align-items: center;

      &:hover,
      &[aria-current] {
        color: ${colors[tokens.hoverColour]};
      }
    }

    > ul {
      display: flex;
      position: relative;
    }

    @media (max-width: ${maxMedia.large}) {
      display: none;
    }
  `};
`;

const StyledBottomLevelNavActions = styled.div`
  ${({ theme: { space, maxMedia } }) => css`
    display: flex;
    align-items: center;
    gap: ${space.large};

    a {
      ${useResponsiveType('bodyMediumBold')};
    }

    @media (max-width: ${maxMedia.large}) {
      display: none;
    }
  `};
`;

const StyledContact = styled.div`
  ${({ theme: { space } }) => css`
    display: flex;
    flex-shrink: 0;
    align-items: center;
    white-space: nowrap;
    gap: ${space.xxSmall};

    a {
      text-decoration: none;
    }
  `};
`;

const StyledMenuIcon = styled.div`
  ${({ theme: { space, media } }) => css`
    display: flex;
    gap: ${space.small};
    margin-left: ${space.small};

    @media (min-width: ${media.small}) {
      gap: ${space.large};
    }

    @media (min-width: ${media.large}) {
      display: none;
    }
  `}
`;

const StyledMenuButton = styled.button`
  ${({ theme: { space } }) => css`
    display: flex;
    gap: ${space.xxSmall};
    align-items: center;
    cursor: pointer;
  `}
`;

export const BottomLevelNav: FC<BottomLevelNavProps> = ({
  activePage,
  brand,
  ctaLink,
  ctaText,
  overrideStickyCTAButton,
  enableMyAccount,
  isDualBrand,
  isMyAccount,
  location,
  loggedIn,
  myAccountLogout,
  showSearch,
  siteId,
}) => {
  const { companyName } = useSiteMetadata();
  const { isMobileNavOpen, toggleMobileNav } = useHeader();
  const dynamicContactNumber = getContactNumberByBrand(brand);

  return (
    <StyledBottomLevelNav>
      <Container>
        <StyledBottomLevelNavPrimary>
          <BrandLogo brand={brand} companyName={companyName} />

          {activePage && !isDualBrand && !isMyAccount && (
            <StyledBottomLevelNavItems aria-label="Main Navigation Second Level">
              {activePage.headerChildItems && (
                <ul>
                  {activePage.headerChildItems.map((childItem, childIndex) => (
                    <li key={childIndex}>
                      {childItem.navigationChildItems &&
                      childItem.navigationChildItems.length > 0 ? (
                        <NavDropdown childItem={childItem} />
                      ) : (
                        <LinkTo to={childItem.link.url}>
                          {childItem.link.label}
                        </LinkTo>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </StyledBottomLevelNavItems>
          )}
        </StyledBottomLevelNavPrimary>

        <StyledBottomLevelNavActions>
          {!activePage?.hideTelephoneNumber && brand !== 'Service Dogs' && (
            <StyledContact>
              <Contact color={tokens.contactIconColor} title="Contact Us" />
              {isMyAccount && (
                <a
                  href={`tel:${
                    activePage?.telephoneNumberOverride
                      ? activePage?.telephoneNumberOverride?.replace(/\s/g, '')
                      : telephoneNumbers.customerServices.replace(/\s/g, '')
                  }`}
                >
                  Call us on{' '}
                  {activePage?.telephoneNumberOverride ||
                    telephoneNumbers.customerServices}{' '}
                </a>
              )}
              {!isMyAccount &&
                dynamicContactNumber &&
                !activePage?.hideTelephoneNumber && (
                  <a href={`tel:${dynamicContactNumber?.replace(/\s/g, '')}`}>
                    {dynamicContactNumber}
                  </a>
                )}
            </StyledContact>
          )}

          {tokens.enableSearch &&
            !isMyAccount &&
            showSearch &&
            !location?.pathname.includes('search') && <SearchModal />}

          {isMyAccount && enableMyAccount && loggedIn && (
            <Button
              label="Log out"
              small
              onClick={myAccountLogout}
              variant={tokens.logOutButtonVariant}
              iconRight={<Logout />}
            />
          )}

          {tokens.enableCallToActionButton &&
            !isMyAccount &&
            brand !== 'Insure K9' &&
            brand !== 'Service Dogs' && (
              <>
                {activePage?.ctaOverride && (
                  <Button
                    url={
                      overrideStickyCTAButton?.url || activePage.ctaOverride.url
                    }
                    label={
                      overrideStickyCTAButton?.label ||
                      activePage.ctaOverride.label
                    }
                    arrowRight
                    small
                    {...(overrideStickyCTAButton?.target && {
                      target: overrideStickyCTAButton.target,
                    })}
                  />
                )}
                {!activePage?.ctaOverride && siteId !== 3162 && (
                  <Button
                    url={
                      overrideStickyCTAButton?.url ||
                      getQuoteUrlByBrand(brand, ctaLink)
                    }
                    label={
                      overrideStickyCTAButton?.label || ctaText || 'Get a quote'
                    }
                    arrowRight
                    small
                    {...(overrideStickyCTAButton?.target && {
                      target: overrideStickyCTAButton.target,
                    })}
                  />
                )}
              </>
            )}
        </StyledBottomLevelNavActions>

        <StyledMenuIcon>
          {tokens.enableSearch &&
            !isMyAccount &&
            showSearch &&
            !location?.pathname.includes('search') && <SearchModal />}

          {!activePage?.hideTelephoneNumber && brand !== 'Service Dogs' && (
            <StyledContact>
              {isMyAccount && (
                <a
                  href={`tel:${
                    activePage?.telephoneNumberOverride
                      ? activePage?.telephoneNumberOverride.replace(/\s/g, '')
                      : telephoneNumbers.customerServices?.replace(/\s/g, '')
                  }`}
                  aria-label={telephoneNumbers.customerServices}
                >
                  <Contact color={tokens.contactIconColor} title="Contact us" />
                </a>
              )}
              {!isMyAccount && dynamicContactNumber && (
                <a
                  href={`tel:${dynamicContactNumber?.replace(/\s/g, '')}`}
                  aria-label={dynamicContactNumber}
                >
                  <Contact color="brandOne100" title="Contact us" />
                </a>
              )}
            </StyledContact>
          )}
          {siteId !== 3162 && (
            <StyledMenuButton
              type="button"
              onClick={toggleMobileNav}
              aria-label="Toggle navigation"
              aria-expanded={isMobileNavOpen}
            >
              {isMobileNavOpen ? (
                <>
                  <Typography component="span">Close</Typography>
                  <Close aria-hidden="true" />
                </>
              ) : (
                <Menu aria-hidden="true" />
              )}
            </StyledMenuButton>
          )}
        </StyledMenuIcon>
      </Container>
    </StyledBottomLevelNav>
  );
};
